import { maxBy, minBy, takeWhile } from 'es-toolkit';

import {
  CalculatedLine,
  Component,
  LineLocation,
  Member,
  MemberLine,
  ParallelLine,
  Point2D as Point2DInterface,
  RelativeMemberGeometry,
} from '@paragon/contract-library';
import {
  Direction2D,
  distanceEquals,
  distanceGreaterThanOrEqual,
  distanceLessThanOrEqual,
  Line2D,
  LineSegment2D,
  Point2D,
  Polygon2D,
  zeroDotProductTolerance,
} from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';
import { throwIfNull } from '@paragon/utilities/lib/utilities';

import { calculateLine as calculateLineFromComponent, findLine } from './lines';
import { calculateRelativeMemberGeometry } from './memberGeometries';
import { oppositeLineLocation } from './utilities';

// This file is a translation of the following server-side code and should be kept in sync.
//  https://github.com/ParagonTruss/ParagonCore/blob/master/servers/libraries/ComponentLibrary/ComponentLibrary/RelativeGeometry/CutAgainstExtensions.cs

class Interval {
  private readonly minEnd: number;
  private readonly maxEnd: number;

  public static get full(): Interval {
    return new Interval(Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY);
  }

  public get base(): number | null {
    return Number.isFinite(this.minEnd) ? this.minEnd : null;
  }

  public get end(): number | null {
    return Number.isFinite(this.maxEnd) ? this.maxEnd : null;
  }

  public get middle(): number | null {
    return this.base == null || this.end == null ? null : (this.base + this.end) / 2;
  }

  public get length(): number | null {
    return this.base == null || this.end == null ? null : this.end - this.base;
  }

  public get isBounded(): boolean {
    return Number.isFinite(this.minEnd) && Number.isFinite(this.maxEnd);
  }

  private constructor(minEnd: number, maxEnd: number) {
    if (minEnd > maxEnd) {
      throw new Error('Malformed interval');
    }

    this.minEnd = minEnd;
    this.maxEnd = maxEnd;
  }

  public static fromEnds(ends: [number, number]): Interval {
    ends = ends[0] > ends[1] ? [ends[1], ends[0]] : ends;
    return new Interval(ends[0], ends[1]);
  }

  public overlaps(other: Interval): boolean {
    return this.minEnd < other.maxEnd && other.minEnd < this.maxEnd;
  }

  public contains(point: number): boolean {
    return this.minEnd < point && point < this.maxEnd;
  }

  public isSupersetOf(interval: Interval): boolean {
    return (
      distanceLessThanOrEqual(this.minEnd, interval.minEnd) && distanceLessThanOrEqual(interval.maxEnd, this.maxEnd)
    );
  }

  public absoluteDistanceTo(point: number): number {
    if (this.contains(point)) return 0;
    if (this.base == null || this.end == null) throw new Error('Interval base or end is null.');

    return Math.max(this.base - point, point - this.end);
  }

  public minus(interval2: Interval): Interval[] {
    const output: Interval[] = [];

    const lowerMin = this.minEnd;
    const lowerMax = Math.min(this.maxEnd, interval2.minEnd);
    if (distanceLessThanOrEqual(lowerMin, lowerMax)) {
      output[output.length] = new Interval(lowerMin, lowerMax);
    }

    const upperMin = Math.max(this.minEnd, interval2.maxEnd);
    const upperMax = this.maxEnd;
    if (distanceLessThanOrEqual(upperMin, upperMax)) {
      output[output.length] = new Interval(upperMin, upperMax);
    }

    return output;
  }

  public lteq(interval2: Interval): boolean {
    if (this.end == null || interval2.base == null) {
      throw new Error('Comparison requires valid ends');
    }

    return this.end <= interval2.base;
  }

  public gteq(interval2: Interval): boolean {
    if (this.base == null || interval2.end == null) {
      throw new Error('Comparison requires valid ends');
    }

    return this.base >= interval2.end;
  }

  public static projectSegmentOnto(segment: LineSegment2D, line: Line2D): Interval {
    return Interval.fromEnds([
      segment.basePoint.projectedSignedDistanceAlong(line),
      segment.endPoint.projectedSignedDistanceAlong(line),
    ]);
  }
}

export interface CutMemberOptions {
  locationHint: Point2DInterface | null;
  guessLineLocation: boolean;
}

export function cutMember(
  geometry: RelativeMemberGeometry,
  component: Component,
  options: CutMemberOptions,
): RelativeMemberGeometry {
  const locationHint = getCutAgainstCavityHint(geometry, component, options.locationHint);

  const lineLocationsToTest: LineLocation[] = options.guessLineLocation
    ? geometry.lineLocation === 'Center'
      ? ['Center', 'Port', 'Starboard']
      : [geometry.lineLocation, 'Center', oppositeLineLocation(geometry.lineLocation)]
    : [geometry.lineLocation];

  return lineLocationsToTest
    .map((lineLocation) => {
      try {
        return cutAgainst({ ...geometry, lineLocation }, component, locationHint);
      } catch {
        return null;
      }
    })
    .filter(notNullish)[0];
}

function getCutAgainstCavityHint(
  geometry: RelativeMemberGeometry,
  component: Component,
  clickedPoint: Point2DInterface | null,
): Point2D {
  // The clicked point serves as a hint any time it exists.
  if (clickedPoint) return Point2D.fromObject(clickedPoint);

  // Otherwise, line types defined by an intersection can use that point as a hint.
  const definitionLine = findLine(geometry.lineGuid, component);
  if (!definitionLine) throw new Error('Could not find line');
  if (
    definitionLine?.lineType === 'PointToPoint' ||
    definitionLine?.lineType === 'PointSlope' ||
    definitionLine?.lineType === 'Perpendicular'
  ) {
    const calculatedLine = calculateLineFromComponent(definitionLine, component);
    if (!calculatedLine) throw new Error('Reference line could not be calculated');
    return calculatedLine.basePoint;
  }

  throw new Error(`Could not find cavity hint for ${definitionLine.lineType}`);
}

export function cutAgainstAll(geometry: RelativeMemberGeometry, component: Component): RelativeMemberGeometry[] {
  return getCavities(geometry, component).map((cavity) => {
    let output = recutOneSide({ ...geometry, guid: crypto.randomUUID() }, cavity, 'BaseCuts', component);
    output = recutOneSide(output, cavity, 'EndCuts', component);
    output = reduceCuts(output, component);
    return output;
  });
}

/**
 * Cut a relative geometry against a truss so that it fits into the cavity closest to `hint`
 */
export function cutAgainst(
  geometry: RelativeMemberGeometry,
  component: Component,
  hint: Point2D,
): RelativeMemberGeometry {
  const midline = calculateLineFromComponent(
    getMemberReferenceLines(
      throwIfNull(component.referenceLines, `Component ${component.guid} does not have referenceLines.`),
      geometry,
    ).center,
    component,
  )!;
  const cavities = getCavities(geometry, component);
  if (cavities.length === 0) {
    throw new Error('Could not find cavity to place member in.');
  }
  const hintAlongGeometryMidline = hint.projectedSignedDistanceAlong(midline);
  const cavity = minBy(cavities, (cavity) => cavity.absoluteDistanceTo(hintAlongGeometryMidline))!;

  let output = recutOneSide(geometry, cavity, 'BaseCuts', component);
  output = recutOneSide(output, cavity, 'EndCuts', component);
  output = reduceCuts(output, component);
  return output;
}

/**
 * Recuts a relative geometry against a truss so that `cutSide` is cut against the truss.
 */
export function cutOneSideAgainst(
  geometry: RelativeMemberGeometry,
  component: Component,
  cutSide: 'BaseCuts' | 'EndCuts',
): RelativeMemberGeometry {
  let polygon = new Polygon2D(
    throwIfNull(calculateRelativeMemberGeometry(geometry, component), 'Invalid geometry.').geometry,
  );

  const definitionLineGuid = throwIfNull(geometry.lineGuid, `Geometry ${geometry.guid} has no defining lineGuid.`);
  const definitionLine = throwIfNull(
    calculateLineFromComponent(
      single(component.referenceLines!.filter((l) => l.guid === definitionLineGuid)),
      component,
    ),
    `Could not calculate defining line ${definitionLineGuid} for geometry ${geometry.guid}.`,
  );
  const getRepresentativeCutPoint = (cutIds: readonly string[]): Point2D =>
    cutIds
      .map((id) =>
        throwIfNull(
          calculateLineFromComponent(single(component.referenceLines!.filter((l) => l.guid === id)), component),
          `Could not calculate line ${id}.`,
        ).intersectWithLine(definitionLine),
      )
      .filter(notNullish)[0];
  const representativeBaseCutPoint = getRepresentativeCutPoint(geometry.baseCutGuids);
  const representativeEndCutPoint = getRepresentativeCutPoint(geometry.endCutGuids);
  const cutOrientation = Direction2D.fromPoints(representativeBaseCutPoint, representativeEndCutPoint);
  if (cutOrientation.dotProduct(definitionLine.direction) < 0) {
    cutSide = cutSide === 'BaseCuts' ? 'EndCuts' : 'BaseCuts';
    geometry = { ...geometry, baseCutGuids: geometry.endCutGuids, endCutGuids: geometry.baseCutGuids };
    polygon = new Polygon2D(
      throwIfNull(calculateRelativeMemberGeometry(geometry, component), 'Invalid geometry.').geometry,
    );
  }

  const memberInterval = Interval.fromEnds([
    Math.min(...polygon.vertices.map((point) => point.projectedSignedDistanceAlong(definitionLine)))!,
    Math.max(...polygon.vertices.map((point) => point.projectedSignedDistanceAlong(definitionLine))),
  ]);
  const cavity = throwIfNull(
    maxBy(getCavities(geometry, component, memberInterval), (cavity) => cavity.end! - cavity.base!),
    'No suitable location found for recut member',
  );

  let output = recutOneSide(geometry, cavity, cutSide, component);
  output = reduceCuts(output, component, cutSide);
  return output;
}

/**
 * Returns all cavities within the search space where a member could be located.
 * Each cavity represents a rectangle with no intersecting members.
 */
function getCavities(geometry: RelativeMemberGeometry, component: Component, searchSpace?: Interval): Interval[] {
  const memberWidthInterval = Interval.fromEnds([0, geometry.width]);
  const memberReferenceLines = getMemberReferenceLines(
    throwIfNull(component.referenceLines, `Component ${component.guid} does not have referenceLines.`),
    geometry,
  );
  const memberPortLine = calculateLineFromComponent(memberReferenceLines.port, component)!;
  const memberStarboardLine = calculateLineFromComponent(memberReferenceLines.starboard, component)!;

  const memberPerpendicular = new Line2D(memberPortLine.basePoint, memberPortLine.direction.rotateReverseQuarterTurn());
  return component.members
    .filter(
      (member) =>
        throwIfNull(member.relativeGeometry, `Member ${member.guid} does not have relativeGeometry.`).guid !==
        geometry.guid,
    )
    .map((member) =>
      new Polygon2D(
        throwIfNull(calculateRelativeMemberGeometry(member.relativeGeometry!, component), 'Invalid geometry.').geometry,
      )
        .slice(memberPortLine)
        .flatMap((slicedOnce) => slicedOnce.slice(memberStarboardLine))
        .filter((slicedTwice) =>
          memberWidthInterval.contains(slicedTwice.centroid.projectedSignedDistanceAlong(memberPerpendicular)),
        ),
    )
    .map((list) => singleOrDefault(list))
    .filter(notNullish)
    .map((memberSlice) =>
      Interval.fromEnds([
        Math.min(...memberSlice.vertices.map((point) => point.projectedSignedDistanceAlong(memberPortLine))),
        Math.max(...memberSlice.vertices.map((point) => point.projectedSignedDistanceAlong(memberPortLine))),
      ]),
    )
    .reduce(
      (cavities, memberOverlap) => cavities.flatMap((cavity) => cavity.minus(memberOverlap)),
      [searchSpace ?? Interval.full],
    )
    .filter((cavity) => cavity.isBounded && !distanceEquals(cavity.length!, 0));
}

/**
 * The meat of `cutAgainstHelpers`, this method recuts a single end of a member, avoiding cutting
 * into `cavity` as much as possible while ensuring no overlap with other members.
 */
function recutOneSide(
  geometry: RelativeMemberGeometry,
  cavity: Interval,
  cutSide: 'BaseCuts' | 'EndCuts',
  component: Component,
): RelativeMemberGeometry {
  const isEnd = cutSide === 'EndCuts';
  const memberWidthInterval = Interval.fromEnds([0, geometry.width]);
  const definitionLine = throwIfNull(
    calculateLineFromComponent(
      single(
        throwIfNull(component.referenceLines, `Component ${component.guid} does not have referenceLines.`).filter(
          (line) => line.guid === geometry.lineGuid,
        ),
      ),
      component,
    ),
    'Definition line cannot be null.',
  );
  const memberLines = getMemberReferenceLines(component.referenceLines!, geometry);
  const memberPortLine = calculateLineFromComponent(memberLines.port, component)!;
  const memberStarboardLine = calculateLineFromComponent(memberLines.starboard, component)!;
  const memberPerpendicular = new Line2D(memberPortLine.basePoint, memberPortLine.direction.rotateReverseQuarterTurn());
  const directionToCutSide = isEnd ? memberPortLine.direction : memberPortLine.direction.reverse();
  const membersWithOutsideEdges = component.members
    .filter((member) => member.relativeGeometry?.guid !== geometry.guid)
    .map((member) => ({
      member,
      edges: getOutsideEdges(member, component, directionToCutSide).filter((edge) =>
        Interval.projectSegmentOnto(edge.segment, memberPerpendicular).overlaps(memberWidthInterval),
      ),
    }));

  const nonIntrusiveCuts = membersWithOutsideEdges
    .map(
      (member) =>
        maxBy(
          member.edges.filter((edge) =>
            [memberPortLine, memberStarboardLine]
              .map((line) => intersectionDistanceAlong(edge.segment, line))
              .every((cutBound) =>
                isEnd
                  ? distanceGreaterThanOrEqual(cutBound, throwIfNull(cavity.end, 'Cavity does not have an end.'))
                  : distanceLessThanOrEqual(cutBound, throwIfNull(cavity.base, 'Cavity does not have a base.')),
              ),
          ),
          (edge) => intersectionDistanceAlong(edge.segment, definitionLine) * (isEnd ? 1 : -1),
        )?.id,
    )
    .filter(notNullish);
  if (nonIntrusiveCuts.length > 0) {
    return withIds(geometry, cutSide, nonIntrusiveCuts);
  }

  const crossingMembers = membersWithOutsideEdges
    .map(({ member, edges }) => ({
      sliced: singleOrDefault(
        new Polygon2D(
          throwIfNull(
            calculateRelativeMemberGeometry(
              throwIfNull(member.relativeGeometry, 'Member does not have relative geometry'),
              component,
            ),
            'Invalid geometry.',
          ).geometry,
        )
          .slice(memberPortLine)
          .flatMap((slicedOnce) => slicedOnce.slice(memberStarboardLine))
          .filter((slicedTwice) =>
            memberWidthInterval.contains(slicedTwice.centroid.projectedSignedDistanceAlong(memberPerpendicular)),
          ),
      ),
      edges,
    }))
    .filter((member): member is typeof member & { sliced: Polygon2D } => member.sliced != null)
    .map((member) => ({
      edges: member.edges,
      lengthInterval: Interval.fromEnds([
        Math.min(...member.sliced.vertices.map((vertex) => vertex.projectedSignedDistanceAlong(definitionLine))),
        Math.max(...member.sliced.vertices.map((vertex) => vertex.projectedSignedDistanceAlong(definitionLine))),
      ]),
      crossesFullWidth: Interval.fromEnds([
        Math.min(...member.sliced.vertices.map((vertex) => vertex.projectedSignedDistanceAlong(memberPerpendicular))),
        Math.max(...member.sliced.vertices.map((vertex) => vertex.projectedSignedDistanceAlong(memberPerpendicular))),
      ]).isSupersetOf(memberWidthInterval),
    }))
    .filter(({ lengthInterval }) => (isEnd ? cavity.lteq(lengthInterval) : cavity.gteq(lengthInterval)))
    .toSorted((member) => member.lengthInterval.middle! * (isEnd ? 1 : -1));

  const members = takeWhile(crossingMembers, (member) => !member.crossesFullWidth);
  const firstCrossingMember = crossingMembers.find((member) => member.crossesFullWidth);
  if (firstCrossingMember) members.push(firstCrossingMember);
  const leastIntrusiveCuts = members
    .map(
      (member) =>
        maxBy(member.edges, (edge) => intersectionDistanceAlong(edge.segment, definitionLine) * (isEnd ? 1 : -1))?.id, // pick cut which allows definition side of member to be longest
    )
    .filter(notNullish);

  return leastIntrusiveCuts.length > 0 ? withIds(geometry, cutSide, leastIntrusiveCuts) : geometry;
}

/**
 * Gets the edges of a member which would be hit first by rays coming from the specified direction.
 */
function getOutsideEdges(
  member: Member,
  component: Component,
  directionToInside: Direction2D,
): { id: string; segment: LineSegment2D }[] {
  const memberRelativeGeometry = throwIfNull(
    member.relativeGeometry,
    `Member ${member.guid} does not have relativeGeometry.`,
  );

  const memberReferenceLines = getMemberReferenceLines(
    throwIfNull(component.referenceLines, `Component ${component.guid} does not have referenceLines.`),
    memberRelativeGeometry,
  );

  const memberPolygon = new Polygon2D(
    throwIfNull(calculateRelativeMemberGeometry(memberRelativeGeometry, component), 'Invalid geometry.').geometry,
  );

  return member
    .relativeGeometry!.baseCutGuids.concat(member.relativeGeometry!.endCutGuids)
    .map((id) => component.referenceLines!.filter((l) => l.guid === id)[0])
    .concat([memberReferenceLines.port, memberReferenceLines.starboard])
    .map((line) => ({
      id: line.guid,
      segment: singleOrDefault(
        memberPolygon.findSegmentIntersectionsWithLine(
          throwIfNull(calculateLineFromComponent(line, component), `Could not calculate line ${line.guid}.`),
        ),
      ),
    }))
    .filter((pair): pair is typeof pair & { segment: LineSegment2D } => pair.segment != null)
    .filter(
      // exclude pairs without matching edges or dot product <= zero angle tolerence
      (pair) =>
        (singleOrDefault(memberPolygon.edges.filter((edge) => pair.segment.equals(edge)))
          ?.direction.rotateQuarterTurn()
          .dotProduct(directionToInside) ?? 0) > zeroDotProductTolerance,
    );
}

function withIds(
  geometry: RelativeMemberGeometry,
  cutSide: 'BaseCuts' | 'EndCuts',
  ids: string[],
): RelativeMemberGeometry {
  switch (cutSide) {
    case 'BaseCuts':
      return { ...geometry, baseCutGuids: ids };
    case 'EndCuts':
      return { ...geometry, endCutGuids: ids };
    default:
      throw new Error('Unknown cut side.');
  }
}

function reduceCuts(
  geometry: RelativeMemberGeometry,
  component: Component,
  cutSide?: 'BaseCuts' | 'EndCuts',
): RelativeMemberGeometry {
  const { referenceLines } = component;

  const polygon = new Polygon2D(
    throwIfNull(calculateRelativeMemberGeometry(geometry, component), 'Invalid geometry.').geometry,
  );

  const reduceCutsOnOneSide = (cutSide0: 'BaseCuts' | 'EndCuts') =>
    withIds(
      geometry,
      cutSide0,
      (cutSide0 === 'BaseCuts' ? geometry.baseCutGuids : geometry.endCutGuids).filter((id) => {
        const line = throwIfNull(
          calculateLineFromComponent(
            throwIfNull(referenceLines?.filter((line) => line.guid === id)[0], `Could not find line ${id}.`),
            component,
          ),
          `Could not calculate line ${id}.`,
        );
        return polygon.edges.filter((edge) => edge.toLine().equals(line)).length > 0;
      }),
    );

  if (cutSide !== 'BaseCuts') {
    geometry = reduceCutsOnOneSide('EndCuts');
  }

  if (cutSide !== 'EndCuts') {
    geometry = reduceCutsOnOneSide('BaseCuts');
  }

  return geometry;
}

function intersectionDistanceAlong(segment: LineSegment2D, line: Line2D): number {
  return throwIfNull(
    segment.toLine().intersectWithLine(line),
    `Lines are parallel:\n\tLine1: ${[
      segment.toLine().basePoint.toArray(),
      segment.toLine().direction.angle,
    ]}\n\tLine2: ${[line.basePoint.toArray(), line.direction.angle]}`,
  ).projectedSignedDistanceAlong(line);
}

function getMemberReferenceLines(
  referenceLines: readonly CalculatedLine[],
  geometry: RelativeMemberGeometry,
): { center: ParallelLine | MemberLine; port: ParallelLine | MemberLine; starboard: ParallelLine | MemberLine } {
  const memberLines = referenceLines.filter(
    (line): line is MemberLine => line.lineType === 'Member' && line.memberGeometryGuid === geometry.guid,
  );

  const center = memberLines.find((line) => line.lineLocation === 'Center');
  const port = memberLines.find((line) => line.lineLocation === 'Port');
  const starboard = memberLines.find((line) => line.lineLocation === 'Starboard');

  if (center && port && starboard) {
    return { center, port, starboard };
  }

  return {
    center: {
      guid: crypto.randomUUID(),
      lineType: 'Parallel',
      lineGuid: geometry.lineGuid,
      offset: offset(geometry.lineLocation, 'Center', geometry.width),
      name: `${geometry.memberName} Center`,
    },
    port: {
      guid: crypto.randomUUID(),
      lineType: 'Parallel',
      lineGuid: geometry.lineGuid,
      offset: offset(geometry.lineLocation, 'Port', geometry.width),
      name: `${geometry.memberName} Port`,
    },
    starboard: {
      guid: crypto.randomUUID(),
      lineType: 'Parallel',
      lineGuid: geometry.lineGuid,
      offset: offset(geometry.lineLocation, 'Starboard', geometry.width),
      name: `${geometry.memberName} Starboard`,
    },
  };
}

function offset(from: LineLocation, to: LineLocation, width: number): number {
  if (from === to) {
    return 0;
  }
  switch (from) {
    case 'Starboard': {
      return to === 'Center' ? width / 2 : width;
    }
    case 'Center': {
      return to === 'Port' ? width / 2 : -width / 2;
    }
    case 'Port': {
      return to === 'Starboard' ? -width : -width / 2;
    }
  }
}

function singleOrDefault<T>(list: T[]): T | null {
  switch (list.length) {
    case 0:
      return null;
    case 1:
      return list[0];
    default:
      throw new Error('More than one item in list.');
  }
}

function single<T>(array: T[]): T {
  return throwIfNull(singleOrDefault(array), 'No items in array.');
}
